import { EventSource } from "eventsource";
import { AxiosRequestConfig } from "axios";
import { axiosInstance, getHeaders } from "../../utils/axios";
import { store } from "../../redux/store/store";
import { selectBackendUrl } from "../../redux/feature/settings/configurations-slice";
import { validateString } from "../../utils/helpers/ObjectCheckerHelper";

import {
  BatchAbortResponse,
  BatchExecuteRequest,
  BatchExecuteResponse,
  BatchResultsResponse,
  SingleScriptResultResponse,
} from "../models/script-runner";

const BASE = "/scriptrunner";

/* ================================
 * BATCH
 * ================================ */
export const executeBatch = async (
  batchId: string,
  req: BatchExecuteRequest,
  cfg?: AxiosRequestConfig
): Promise<BatchExecuteResponse> => {
  console.debug("[executeBatch]", { batchId, req });
  const result = await axiosInstance.post(`${BASE}/batch/${encodeURIComponent(batchId)}/execute`, req, {
    ...cfg,
    params: { ...cfg?.params, dryRun: false },
  });
  return result.data;
};

export const joinBatch = (batchId: string, timeout?: number, cfg?: AxiosRequestConfig): Promise<BatchResultsResponse> =>
  axiosInstance
    .get(`${BASE}/batch/${encodeURIComponent(batchId)}/join`, {
      ...cfg,
      params: { ...cfg?.params, ...(timeout != null ? { timeout } : {}) },
      // A join is lifecycle-sensitive: a cached terminal/non-terminal snapshot can never answer a
      // later long-poll correctly. Bypass the response cache while retaining in-flight deduplication.
      headers: { ...cfg?.headers, "x-cache-ttl": "0" },
    })
    .then((r) => r.data);

/** Optional filters/conditional-fetch options for `fetchBatchResults` (06-api-contract.md §5). */
export interface FetchBatchResultsOptions {
  offset?: number;
  limit?: number;
  status?: string;
  /** Last-seen ETag for this batch's results, if any - sent as `If-None-Match` for a cheap `304`. */
  etag?: string;
}

/**
 * `fetchBatchResults`'s resolved value, additively extending `BatchResultsResponse` with the
 * response's own `ETag` header when present, so a caller can feed it back as `opts.etag` on the
 * next poll for a cheap `304`.
 */
export interface BatchResultsWithEtag extends BatchResultsResponse {
  etag?: string;
}

/**
 * Paged results polling fallback (`GET /batch/{id}/results`). Supports the same
 * `offset`/`limit`/`status` filters the backend exposes, plus an ETag-conditional request: when
 * `opts.etag` is supplied it's sent as `If-None-Match` so an unchanged batch can respond `304`
 * cheaply. A non-2xx response simply rejects — no retrying here, the caller owns any retry policy.
 * The resolved value additionally carries the response's `ETag` header (case-insensitive lookup)
 * as `.etag`, to feed back into the next call.
 */
export const fetchBatchResults = (
  batchId: string,
  opts?: FetchBatchResultsOptions,
  cfg?: AxiosRequestConfig
): Promise<BatchResultsWithEtag> =>
  axiosInstance
    .get(`${BASE}/batch/${encodeURIComponent(batchId)}/results`, {
      ...cfg,
      params: {
        ...cfg?.params,
        ...(opts?.offset != null ? { offset: opts.offset } : {}),
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
        ...(opts?.status != null ? { status: opts.status } : {}),
      },
      headers: {
        ...cfg?.headers,
        // ETag changes live in request headers, while the shared GET cache key intentionally does
        // not include headers. Always bypass response caching so each poll reaches the server.
        "x-cache-ttl": "0",
        ...(opts?.etag ? { "If-None-Match": opts.etag } : {}),
      },
    })
    .then((r) => ({ ...r.data, etag: r.headers?.["etag"] ?? r.headers?.["ETag"] }));

/**
 * Aborts a running batch. Safe to call even if the batch already completed — callers tolerate a
 * failed abort request, marking rows CANCELED locally while the batch expires server-side. Must
 * not retry internally — rejections propagate to the caller as-is.
 */
export const abortBatch = async (batchId: string, cfg?: AxiosRequestConfig): Promise<BatchAbortResponse> => {
  const result = await axiosInstance.post(`${BASE}/batch/${encodeURIComponent(batchId)}/abort`, undefined, cfg);
  return result.data;
};

/* ================================
 * STREAMING (SSE)
 * ================================ */

/**
 * Joins an absolute backend base url with an absolute-path segment (starting with `/`), producing
 * exactly one `/` at the seam regardless of trailing slashes in `base`.
 *
 * Trims trailing slashes with a manual scan rather than a `/\/+$/` regex: that pattern is
 * unanchored at the start with an unbounded quantifier ahead of a can-fail `$` anchor, which is
 * exactly the shape that causes quadratic backtracking (e.g. `base` being many slashes followed
 * by a non-slash character) - see SonarQube rule S5852. The loop below is linear with no
 * backtracking regardless of input.
 */
function joinBaseUrl(base: string, path: string): string {
  let end = base.length;
  while (end > 0 && base[end - 1] === "/") {
    end--;
  }
  return `${base.slice(0, end)}${path}`;
}

/**
 * Resolves the same absolute backend base url `axiosInstance`'s request interceptor assigns to
 * `config.baseURL`. `EventSource` has no notion of a shared `baseURL` — a relative url resolves
 * against the page origin, never the backend (see `streamBatch`'s doc comment). Returns
 * `undefined`, never throws, when the value is missing/blank or the store read fails.
 */
function resolveStreamBaseUrl(): string | undefined {
  try {
    const backendUrl = selectBackendUrl(store.getState());
    return validateString(backendUrl) ? backendUrl : undefined;
  } catch {
    return undefined;
  }
}

/** Header name `mergeFetchHeaders` treats specially - see its own doc comment. Compared
 * case-insensitively, so this lower-case form matches any casing (`Last-Event-Id`, `Last-Event-ID`, ...). */
const LAST_EVENT_ID_HEADER = "last-event-id";

/**
 * Case-insensitively merges the `eventsource` polyfill's own per-request `initHeaders` with
 * `overrideHeaders` (auth headers, and optionally our own initial `Last-Event-Id`), so a header
 * differing only by casing never produces two entries on the wire.
 *
 * `overrideHeaders` wins for every key except `Last-Event-Id`: when `initHeaders` already carries
 * any casing of that header, its value is preserved — the polyfill sets its own `Last-Event-Id` on
 * reconnect after a dropped connection, carrying the actual last-seen event id (the whole point of
 * an SSE resume), which must never be overwritten by our caller-supplied initial cursor.
 */
function mergeFetchHeaders(
  initHeaders: Record<string, string>,
  overrideHeaders: Record<string, string>
): Record<string, string> {
  const merged: Record<string, string> = {};
  const keyByLowerCase = new Map<string, string>();
  for (const [key, value] of Object.entries(initHeaders)) {
    const lowerCaseKey = key.toLowerCase();
    const existingKey = keyByLowerCase.get(lowerCaseKey);
    if (existingKey !== undefined) {
      delete merged[existingKey];
    }
    merged[key] = value;
    keyByLowerCase.set(lowerCaseKey, key);
  }

  for (const [overrideKey, value] of Object.entries(overrideHeaders)) {
    const lowerCaseKey = overrideKey.toLowerCase();

    if (lowerCaseKey === LAST_EVENT_ID_HEADER && keyByLowerCase.has(lowerCaseKey)) {
      continue; // the polyfill's own reconnect cursor is already present - never overwrite it.
    }

    const existingKey = keyByLowerCase.get(lowerCaseKey);
    if (existingKey !== undefined && existingKey !== overrideKey) {
      delete merged[existingKey]; // drop the differently-cased key so no duplicate survives.
    }
    merged[overrideKey] = value;
    keyByLowerCase.set(lowerCaseKey, overrideKey);
  }

  return merged;
}

/** Handlers `streamBatch` invokes for the named SSE events of a single batch's result stream. */
export interface StreamBatchHandlers {
  /** `script-result` event: fired once per script, with the JSON-parsed payload. */
  onScriptResult: (result: SingleScriptResultResponse) => void;
  /** `progress` event: fired with the JSON-parsed payload. Optional - progress is advisory only. */
  onProgress?: (progress: unknown) => void;
  /**
   * `batch-completed` event: fired once, with no payload. The backend closes the stream
   * server-side right after emitting this — `streamBatch` does not auto-`close()` on it, so the
   * caller stays in control of when to tear down.
   */
  onCompleted: () => void;
  /**
   * Fired for: (1) a transport-level `error` event from the EventSource itself, (2) a `JSON.parse`
   * failure while handling `script-result`/`progress`, or (3) a failure resolving auth headers before
   * the connection could even be opened. The raw event/error is passed through unmodified.
   */
  onError: (error: unknown) => void;
}

/**
 * Opens a self-managed, per-call SSE subscription to a single batch's result stream
 * (`GET {BASE}/batch/{id}/stream`) and returns a `{ close }` handle to tear it down.
 *
 * The url passed to `EventSource` must be absolute: `EventSource` has no notion of axios's
 * `baseURL`, so a relative url would resolve against the page origin instead of the backend.
 * `resolveStreamBaseUrl()`/`joinBaseUrl()` resolve and join the same absolute base
 * `axiosInstance` uses, falling back to the relative path only when the base is unavailable.
 *
 * Constructs its own `EventSource` rather than routing through `EventsHelper.subscribeToHost`,
 * which keeps a single module-level connection and would kill a sibling batch's stream the moment
 * a second one starts. Every `streamBatch` call is fully independent.
 *
 * Auth is injected via a custom `fetch` override (the only way to attach headers to an SSE
 * request, since `EventSource` itself has no headers option), merging tokens into every request
 * the `eventsource` polyfill makes via `mergeFetchHeaders` (case-insensitive, see its own doc
 * comment). `opts.lastEventId` is merged in as `Last-Event-Id`, but only for a connection's first
 * attempt — a polyfill-driven reconnect already carries its own resume cursor, which always wins.
 *
 * Header resolution is asynchronous, but callers need the `{ close }` handle back synchronously
 * (e.g. to cancel a stream requested and immediately abandoned). Resolution + `EventSource`
 * construction + listener wiring happen inside a fire-and-forget async closure that re-checks a
 * shared `closed` flag right after `await getHeaders(...)` settles, before opening the connection
 * — so a caller that calls `close()` synchronously right after receiving the handle is guaranteed
 * to win the race: no `EventSource` is ever constructed and no handler ever fires. `close()` itself
 * is a plain, synchronous, idempotent function.
 */
export const streamBatch = (
  batchId: string,
  handlers: StreamBatchHandlers,
  opts?: { lastEventId?: string }
): { close: () => void } => {
  let closed = false;
  let source: EventSource | undefined;

  const handleScriptResult = (event: MessageEvent): void => {
    try {
      const parsed = JSON.parse(event.data) as SingleScriptResultResponse;
      handlers.onScriptResult(parsed);
    } catch (e) {
      console.error(`[streamBatch] failed to parse "script-result" payload: ${event.data}`, e);
      handlers.onError(e);
    }
  };

  const handleProgress = (event: MessageEvent): void => {
    try {
      const parsed = JSON.parse(event.data);
      handlers.onProgress?.(parsed);
    } catch (e) {
      console.error(`[streamBatch] failed to parse "progress" payload: ${event.data}`, e);
      handlers.onError(e);
    }
  };

  const handleCompleted = (): void => {
    handlers.onCompleted();
  };

  const handleTransportError = (event: Event): void => {
    handlers.onError(event);
  };

  (async () => {
    let authHeaders: Record<string, string>;
    try {
      authHeaders = await getHeaders(false);
    } catch (e) {
      if (!closed) handlers.onError(e);
      return;
    }

    if (closed) return; // caller closed before headers resolved - never connect.

    const requestHeaders = opts?.lastEventId ? { ...authHeaders, "Last-Event-Id": opts.lastEventId } : authHeaders;

    const streamPath = `${BASE}/batch/${encodeURIComponent(batchId)}/stream`;
    const backendUrl = resolveStreamBaseUrl();
    if (!backendUrl) {
      console.error(
        `[streamBatch] could not resolve an absolute backend base url - falling back to the relative url "${streamPath}", which 404s unless the page origin is itself the backend`
      );
    }
    const streamUrl = backendUrl ? joinBaseUrl(backendUrl, streamPath) : streamPath;

    try {
      source = new EventSource(streamUrl, {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: mergeFetchHeaders(init.headers, requestHeaders),
          }),
      });
    } catch (e) {
      handlers.onError(e);
      return;
    }

    source.addEventListener("script-result", handleScriptResult);
    source.addEventListener("progress", handleProgress);
    source.addEventListener("batch-completed", handleCompleted);
    source.addEventListener("error", handleTransportError);
  })();

  const close = (): void => {
    if (closed) return; // idempotent: no-op once already closed (or never opened).
    closed = true;

    if (source) {
      source.removeEventListener("script-result", handleScriptResult);
      source.removeEventListener("progress", handleProgress);
      source.removeEventListener("batch-completed", handleCompleted);
      source.removeEventListener("error", handleTransportError);
      source.close();
    }
  };

  return { close };
};
